import { css } from 'styled-components'

/**
 * The Pulse V2 screen's whole look, lifted verbatim from the design prototype
 * rather than retyped, so the screen and the design cannot drift apart. The
 * only edits are the ones the app forces: `:root`, `html` and `body` become
 * the wrapper's own scope, and the tile holds real token art instead of the
 * prototype's sprite sheet.
 *
 * Source: https://claude.ai/artifact/5DHS184wUvyGV3PGQTbvLS
 *
 * Nothing here reads a terminal theme token on purpose. This screen answers to
 * its own palette, which is the whole point of putting it beside the old one.
 */
export const pulseV2Styles = css`
  & {
    --gutter: 24px;
    --bg: #0b0c0f;
    --panel: #101114;
    --head: #0e0f13;
    --chip: #16171c;
    --chip-hi: #22242d;
    --line: #1b1d24;
    --hair: rgba(255, 255, 255, 0.055);
    --t1: #fcfcfc;
    --t2: #c8c9d1;
    --t3: #8a8c98;
    --t4: #5e6070;
    --t5: #3c3e4a;
    --up: #46b87d;
    --down: #e15c5c;
    --warn: #e0a33c;
    --accent: #526fff;
    --colmin: 420px;
  }
  * {
    box-sizing: border-box;
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  && {
    background: var(--bg);
    color: var(--t1);
    font:
      500 12px/1.1 Geist,
      system-ui,
      sans-serif;
    -webkit-font-smoothing: antialiased;
    overflow: hidden;
  }
  button {
    font-family: inherit;
    border: 0;
    background: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
  }
  .mono {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.1px;
  }
  .io {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .is {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    fill: currentColor;
    stroke: none;
  }
  .lb {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--t4);
  }
  .ib {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 5px;
    color: var(--t4);
    flex-shrink: 0;
  }

  && {
    container-type: inline-size;
    container-name: pulse;
  }
  .app {
    --gutter: 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    background: var(--bg);
  }
  .glow {
    /*
     * The prototype centred the ellipse on the box's bottom edge, so the
     * gradient was brightest exactly where the box stopped — a straight cut
     * across the page just above the column heads. The ellipse now sits inside
     * its box and finishes fading well before the edge.
     */
    position: absolute;
    left: 0;
    right: 0;
    top: -320px;
    height: 700px;
    background: radial-gradient(
      900px 350px at 50% 50%,
      rgba(82, 111, 255, 0.1),
      transparent 70%
    );
    pointer-events: none;
  }

  /* ---------- chrome ---------- */
  .nav {
    position: relative;
    height: 56px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 var(--gutter);
    border-bottom: 1px solid var(--line);
  }
  .mark {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    flex-shrink: 0;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }
  .brand {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.3px;
    margin-right: 6px;
  }
  .links {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .links,
  .nv {
    flex-shrink: 0;
  }
  .links a {
    height: 28px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--t3);
    display: flex;
    align-items: center;
  }
  .links a.on {
    color: #8fa0ff;
  }
  .search {
    width: 250px;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid var(--line);
    background: var(--panel);
    display: flex;
    align-items: center;
    gap: 7px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .search input {
    flex-grow: 1;
    width: 0;
    border: 0;
    background: none;
    outline: none;
    color: var(--t1);
    font-size: 12px;
    font-family: inherit;
  }
  .pill {
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    background: var(--panel);
    border: 1px solid var(--line);
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--t2);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
    flex-shrink: 0;
  }
  .cta {
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    background: var(--accent);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.22),
      0 1px 10px rgba(82, 111, 255, 0.28);
  }
  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: linear-gradient(135deg, #526fff, #46b87d);
    flex-shrink: 0;
  }
  .navright {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .burger {
    display: none;
  }

  .bar {
    position: relative;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 var(--gutter);
    border-bottom: 1px solid var(--line);
  }
  .bar h1 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.3px;
  }
  .seg {
    display: flex;
    align-items: center;
    gap: 1px;
    padding: 2px;
    border-radius: 8px;
    background: var(--panel);
    border: 1px solid var(--line);
    flex-shrink: 0;
  }
  .seg button {
    height: 22px;
    padding: 0 9px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 500;
    color: var(--t4);
  }
  .seg button.on {
    background: var(--chip-hi);
    color: var(--t1);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);
  }
  .barright {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* the phone's column switch — only rendered narrow */
  .tabs {
    display: none;
    flex-shrink: 0;
    padding: 8px var(--gutter);
  }
  .tabs .wrap {
    display: flex;
    gap: 2px;
    width: 100%;
    height: 36px;
    padding: 3px;
    border-radius: 9999px;
    background: var(--panel);
    border: 1px solid var(--line);
  }
  .tb {
    flex-grow: 1;
    height: 30px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--t3);
  }
  .tb.on {
    background: var(--chip-hi);
    color: var(--t1);
    font-weight: 600;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);
  }
  .tb .mono {
    font-size: 10px;
    color: var(--t4);
  }

  .lit {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .dot {
    width: 5px;
    height: 5px;
    border-radius: 9999px;
    flex-shrink: 0;
  }
  .nv {
    height: 28px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--t3);
    display: flex;
    align-items: center;
  }
  .nv:hover {
    color: var(--t2);
    background: var(--chip);
  }
  .nv.on {
    color: #8fa0ff;
  }
  .kbd {
    height: 17px;
    padding: 0 5px;
    border-radius: 4px;
    background: var(--chip-hi);
    color: var(--t4);
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }
  .chains {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .cn {
    width: 26px;
    height: 26px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.42;
    flex-shrink: 0;
  }
  .cn.on {
    opacity: 1;
  }
  .qb {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 8px;
    border-radius: 9999px;
    border: 1px solid var(--hair);
    color: var(--t2);
    flex-shrink: 0;
  }
  .qb .mono {
    font-size: 10.5px;
    font-weight: 600;
  }
  .rate {
    font-size: 10px;
    color: var(--t5);
  }
  @keyframes breathe {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
  .live .dot {
    animation: breathe 2.4s ease-in-out infinite;
  }

  /* ---------- the column strip ---------- */
  .cols {
    flex-grow: 1;
    min-height: 0;
    padding: 0 var(--gutter);
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .col {
    min-width: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--line);
    overflow: hidden;
  }
  .col:last-child {
    border-right: 0;
  }
  .hd {
    height: 36px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 0 16px;
    border-bottom: 1px solid var(--line);
    background: linear-gradient(180deg, #131419, var(--head));
  }
  .hd h2 {
    margin: 0;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: -0.1px;
  }
  .cnt {
    font-size: 11px;
    font-weight: 500;
    color: var(--t4);
  }
  .live {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 18px;
    padding: 0 6px 0 5px;
    border-radius: 9999px;
    background: rgba(70, 184, 125, 0.1);
  }
  .live i {
    width: 5px;
    height: 5px;
    border-radius: 9999px;
    background: var(--up);
  }
  .live b {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    color: var(--up);
  }
  .hdr {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .list {
    flex-grow: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* ---------- the row ----------
   No side stack: V and MC ride the ticker line, the way axiom does it. That
   buys back ~100px, which is why three columns now fit where two did. */
  .rw {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 14px 9px;
    border-bottom: 1px solid var(--line);
  }
  .top {
    display: flex;
    gap: 9px;
  }
  .avw {
    position: relative;
    flex-shrink: 0;
    width: 68px;
    height: 68px;
    border-radius: 8px;
    padding: 2px;
  }
  .av {
    width: 64px;
    height: 64px;
    border-radius: 6px;
    overflow: hidden;
    display: grid;
    place-items: center;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }
  .plat {
    position: absolute;
    right: -3px;
    bottom: -3px;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #15161b;
    border: 2px solid var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .plat b {
    font-size: 8px;
    font-weight: 700;
  }
  .bd {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding-top: 1px;
  }

  .ln1 {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }
  .tk {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.2px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .nm {
    font-size: 11px;
    font-weight: 400;
    color: var(--t4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .vm {
    margin-left: auto;
    display: flex;
    align-items: baseline;
    gap: 4px;
    flex-shrink: 0;
  }
  .vm b {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--t2);
  }
  .vm .mc {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: var(--t1);
  }
  .vm .chg {
    font-size: 11px;
    font-weight: 500;
  }

  .ln2,
  .ln3 {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 16px;
  }
  .cl {
    display: flex;
    align-items: center;
    gap: 3px;
    color: var(--t4);
    flex-shrink: 0;
  }
  .cl > b {
    color: var(--t2);
    font-size: 11px;
    font-weight: 500;
  }
  .soc {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    color: var(--t5);
  }
  .soc a {
    display: flex;
  }
  .soc a:hover {
    color: var(--t2);
  }
  .spark {
    flex-shrink: 0;
    width: 52px;
    height: 16px;
    margin-left: auto;
  }
  .caw {
    display: flex;
    align-items: center;
    gap: 3px;
    color: var(--t5);
    flex-shrink: 0;
  }
  .caw b {
    font-size: 10px;
  }
  .caw:hover {
    color: var(--t2);
  }
  .ln3 .pad {
    font-size: 10.5px;
    color: var(--t4);
    flex-shrink: 0;
  }
  .ln3 .hnd {
    font-size: 10.5px;
    color: #6e82c8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex-shrink: 1;
  }

  .pills {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    min-width: 0;
  }
  .pl {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 7px;
    border-radius: 9999px;
    border: 1px solid var(--hair);
    flex-shrink: 0;
    color: var(--t4);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
  }
  .pl b {
    font-size: 10.5px;
    font-weight: 500;
    color: var(--t2);
  }
  .pl i {
    font-style: normal;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--t4);
  }
  .buy {
    height: 24px;
    padding: 0 9px;
    border-radius: 9999px;
    border: 1px solid var(--hair);
    background: var(--chip);
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--t1);
    flex-shrink: 0;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .buy:hover {
    background: var(--chip-hi);
  }
  .buy .mono {
    font-size: 11px;
    font-weight: 600;
  }
  .mbuy {
    margin-left: auto;
  }

  .pills {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    min-width: 0;
  }
  .pl {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 7px;
    border-radius: 9999px;
    border: 1px solid var(--hair);
    flex-shrink: 0;
    color: var(--t4);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
  }
  .pl b {
    font-size: 10.5px;
    font-weight: 500;
    color: var(--t2);
  }
  .pl i {
    font-style: normal;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--t4);
  }
  .caw b {
    color: var(--t5);
  }

  /* the phone's own tab bar */
  .tabbar {
    display: none;
    flex-shrink: 0;
    height: 64px;
    padding: 8px 6px 0;
    border-top: 1px solid var(--line);
    background: var(--head);
  }
  .tabbar a {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: var(--t4);
  }
  .tabbar a.on {
    color: var(--t1);
  }
  .tabbar a span {
    font-size: 10px;
    font-weight: 500;
  }
  .tabbar .io,
  .tabbar .is {
    width: 19px;
    height: 19px;
  }

  .foot {
    position: relative;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 var(--gutter);
    border-top: 1px solid var(--line);
  }
  .fl {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--t4);
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }
  .fl b {
    color: var(--t2);
    font-weight: 500;
  }
  .footright {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* ============ the ladder ============
   With V and MC on the ticker line the row no longer needs a 92px side stack,
   so a column works down to ~360px. That is why three columns now appear at
   1160 instead of 1280, and why 4K gets five feeds instead of five wide rows. */

  .col[data-col='trending'],
  .col[data-col='gainers'] {
    display: none;
  }

  @container pulse (min-width:1960px) {
    .app {
      --gutter: 40px;
    }
    .cols {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
    .col[data-col='trending'],
    .col[data-col='gainers'] {
      display: flex;
    }
  }
  @container pulse (min-width:1560px) and (max-width:1959px) {
    .app {
      --gutter: 32px;
    }
    .cols {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    .col[data-col='trending'] {
      display: flex;
    }
  }
  @container pulse (min-width:3300px) {
    .cols {
      grid-template-columns: repeat(5, minmax(0, 700px));
      justify-content: center;
    }
  }

  /* each step gives up the least-consulted reading first */
  @container pulse (max-width:1479px) {
    .pl.ath {
      display: none;
    }
    .soldelta {
      display: none;
    }
  }
  @container pulse (max-width:1319px) {
    .cl.fee,
    .cl.mk {
      display: none;
    }
    .kbd,
    .rate {
      display: none;
    }
    .cn:not(.on) {
      display: none;
    }
    .dispbtn span {
      display: none;
    }
    .dispbtn {
      padding: 0 9px;
    }
  }
  @container pulse (max-width:1159px) {
    .cols {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .col[data-col='migrated'] {
      display: none;
    }
    .caw {
      display: none;
    }
    .search {
      width: 200px;
    }
    .fl.drop {
      display: none;
    }
  }
  @container pulse (max-width:1023px) {
    .links,
    .nv {
      display: none;
    }
    .burger {
      display: flex;
    }
    .search {
      display: none;
    }
    .seg.density {
      display: none;
    }
  }
  @container pulse (max-width:759px) {
    .app {
      --gutter: 16px;
    }
    .cols {
      grid-template-columns: minmax(0, 1fr);
    }
    .col[data-col='final'] {
      display: none;
    }
    .tabs {
      display: flex;
    }
    .col {
      border-right: 0;
    }
  }

  /* under 640 — the phone: bigger art, fewer readings, a thumb-sized buy */
  @container pulse (max-width:639px) {
    .app {
      --gutter: 12px;
    }
    .bar,
    .foot {
      display: none;
    }
    .tabbar {
      display: flex;
    }
    .nav {
      height: 52px;
    }
    .cta,
    .pill,
    .qb {
      display: none;
    }
    .rw {
      padding: 12px var(--gutter) 10px;
      gap: 7px;
    }
    .avw {
      width: 80px;
      height: 80px;
      border-radius: 10px;
    }
    .av {
      width: 76px;
      height: 76px;
      border-radius: 8px;
    }
    .tk {
      font-size: 16px;
    }
    .vm .mc {
      font-size: 15px;
    }
    .cl.drop,
    .cl.crown,
    .pl.ins,
    .pl.bundle {
      display: none;
    }
    .pills {
      height: 26px;
    }
    .pl {
      height: 26px;
      padding: 0 8px;
    }
    .buy {
      height: 26px;
      padding: 0 12px;
      background: var(--accent);
      color: #fff;
      border-color: transparent;
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.24),
        0 1px 8px rgba(82, 111, 255, 0.26);
    }
  }
  @container pulse (max-width:389px) {
    .pl:nth-of-type(3) {
      display: none;
    }
  }
`
